/* eslint-disable react/display-name */
import { List, ListItem, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Map } from "ol";
import React from "react";
import { useTranslation } from "react-i18next";
import { LayerGroupTitle, LayerTitle } from "../types/layers";
import DraggableSettingsList from "./DraggableSettingsList";
import { AtonExtraSettings } from "./extra-settings/AtonExtraSettings";
import { IceLevelExtraSettings } from "./extra-settings/IceLevelExtraSettings";
import { ProjectExtraSettings } from "./extra-settings/ProjectsExtraSettings";
import { SeaSurveyExtraSettings } from "./extra-settings/SeaSurveyExtraSettings";
import FleetLayerSelection from "./FleetLayerSelection";
import SettingsAccordion from "./SettingsAccordion";

const useStyles = makeStyles((theme) => ({
  title: {
    color: theme.palette.primary.contrastText,
  },
}));

interface Props {
  map: Map;
}

export type LayerExtraSettings = {
  title: LayerTitle;
  content: JSX.Element;
};

const LayerSettings = React.forwardRef<HTMLUListElement, Props>(
  ({ map }: Props, ref): JSX.Element => {
    const classes = useStyles();
    const { t } = useTranslation("map");

    const layerExtraSettings: LayerExtraSettings[] = React.useMemo(
      () => [
        {
          title: LayerTitle.AtoNs,
          content: <AtonExtraSettings />,
        },
        {
          title: LayerTitle.Measurements,
          content: <SeaSurveyExtraSettings />,
        },
        {
          title: LayerTitle.IceLevel,
          content: <IceLevelExtraSettings />,
        },
        {
          title: LayerTitle.Projects,
          content: <ProjectExtraSettings />,
        },
      ],
      [],
    );

    return (
      <List ref={ref} sx={{ height: "100%", overflow: "auto" }}>
        <ListItem>
          <Typography variant="h4" className={classes.title}>
            {t("layer-settings-title")}
          </Typography>
        </ListItem>
        <SettingsAccordion map={map} title={LayerGroupTitle.BackgroundMap}>
          <DraggableSettingsList
            map={map}
            title={LayerGroupTitle.BackgroundMap}
          />
        </SettingsAccordion>
        <SettingsAccordion map={map} title={LayerGroupTitle.Ports}>
          <DraggableSettingsList map={map} title={LayerGroupTitle.Ports} />
        </SettingsAccordion>
        <SettingsAccordion map={map} title={LayerGroupTitle.Events}>
          <DraggableSettingsList map={map} title={LayerGroupTitle.Events} />
        </SettingsAccordion>
        <SettingsAccordion map={map} title={LayerGroupTitle.DamsAndFerries}>
          <DraggableSettingsList
            map={map}
            title={LayerGroupTitle.DamsAndFerries}
          />
        </SettingsAccordion>
        <SettingsAccordion
          map={map}
          title={LayerGroupTitle.VesselsAndEquipment}
        >
          <DraggableSettingsList
            map={map}
            title={LayerGroupTitle.VesselsAndEquipment}
          />
        </SettingsAccordion>
        <SettingsAccordion map={map} title={LayerGroupTitle.Fleets}>
          <FleetLayerSelection />
        </SettingsAccordion>
        <SettingsAccordion map={map} title={LayerGroupTitle.Measurements}>
          <DraggableSettingsList
            map={map}
            title={LayerGroupTitle.Measurements}
            extraSettings={layerExtraSettings}
          />
        </SettingsAccordion>
        <SettingsAccordion map={map} title={LayerGroupTitle.Waterways}>
          <DraggableSettingsList map={map} title={LayerGroupTitle.Waterways} />
        </SettingsAccordion>
        <SettingsAccordion
          map={map}
          title={LayerGroupTitle.WeatherAndConditions}
        >
          <DraggableSettingsList
            map={map}
            title={LayerGroupTitle.WeatherAndConditions}
            extraSettings={layerExtraSettings}
          />
        </SettingsAccordion>
        <SettingsAccordion map={map} title={LayerGroupTitle.AtoNs}>
          <DraggableSettingsList
            map={map}
            title={LayerGroupTitle.AtoNs}
            extraSettings={layerExtraSettings}
          />
        </SettingsAccordion>
      </List>
    );
  },
);

export default LayerSettings;
